<template>
  <div class="relative h-full flex flex-col overflow-hidden">
    <!-- Mobile Backdrop with Backdrop Blur -->
    <transition name="fade">
      <div
        v-if="showMobileSidebar"
        @click="closeMobileSidebar"
        class="fixed inset-0 bg-slate-950/60 backdrop-blur-xs z-[998] lg:hidden"
      ></div>
    </transition>

    <!-- Sidebar Container -->
    <aside
      class="sidebar z-[999] transition-all duration-300 ease-in-out flex flex-col h-full max-h-screen bg-white dark:bg-[#0E1526] border-r border-slate-200/80 dark:border-[#1E293B] shadow-sm select-none"
      :class="[
        // Desktop Rail vs Full
        'lg:relative lg:translate-x-0',
        isCollapsed ? 'lg:w-[72px]' : 'lg:w-64',
        // Mobile Slide-over Sheet
        'fixed inset-y-0 left-0 w-72 max-w-[85vw]',
        showMobileSidebar ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0',
      ]"
    >
      <!-- BRAND / HEADER -->
      <div
        class="h-16 px-4 border-b border-slate-100 dark:border-[#1E293B] flex items-center justify-between shrink-0"
      >
        <router-link
          to="/"
          class="flex items-center gap-3 min-w-0 group"
          :title="isCollapsed ? 'Educanium' : ''"
        >
          <!-- Modern Brand Icon -->
          <div
            class="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 via-indigo-500 to-violet-500 text-white flex items-center justify-center shadow-md shadow-indigo-500/25 ring-2 ring-indigo-500/10 shrink-0 group-hover:scale-105 transition-transform"
          >
            <GraduationCap class="w-5 h-5 text-white" />
          </div>

          <!-- Brand Text Stack -->
          <div
            v-if="!isCollapsed"
            class="min-w-0 transition-opacity duration-200"
          >
            <div class="flex items-center gap-1.5">
              <span class="text-base font-extrabold text-slate-900 dark:text-white tracking-tight leading-none">
                Educanium
              </span>
              <span class="w-2 h-2 rounded-full bg-emerald-500 glow-dot-emerald shrink-0" title="System Operational"></span>
            </div>
            <p class="text-[11px] font-semibold text-slate-400 dark:text-slate-500 tracking-wide uppercase mt-1">
              School OS
            </p>
          </div>
        </router-link>

        <!-- Close Button (Mobile Only) -->
        <button
          type="button"
          @click="closeMobileSidebar"
          class="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          aria-label="Close sidebar"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- NAVIGATION SCROLLER -->
      <nav class="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-6 custom-scrollbar">
        <!-- 1. CORE SECTION -->
        <div class="space-y-1">
          <RouterLink
            v-for="item in mainNavigationItems"
            :key="item.id"
            :to="item.route"
            class="block"
            @click="handleNavClick(item.id)"
          >
            <div
              :class="[
                'group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150',
                activeItem === item.id
                  ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/70 dark:hover:bg-slate-800/60',
                isCollapsed ? 'justify-center px-0' : '',
              ]"
              :title="isCollapsed ? item.label : ''"
            >
              <!-- Active Indicator Bar -->
              <div
                v-if="activeItem === item.id"
                class="absolute left-0 top-1.5 bottom-1.5 w-1 bg-indigo-600 dark:bg-indigo-400 rounded-r-full"
              ></div>

              <component
                :is="item.icon"
                :class="[
                  'w-5 h-5 shrink-0 transition-colors',
                  activeItem === item.id
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-700 dark:group-hover:text-slate-300',
                ]"
              />

              <span
                v-if="!isCollapsed"
                class="truncate flex-1 tracking-tight"
              >
                {{ item.label }}
              </span>
            </div>
          </RouterLink>
        </div>

        <!-- 2. ACADEMICS SECTION -->
        <div class="space-y-1">
          <div
            v-if="!isCollapsed"
            class="px-3 pb-1 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider"
          >
            Academics
          </div>
          <div
            v-else
            class="w-full h-px bg-slate-200 dark:bg-slate-800 my-2"
          ></div>

          <RouterLink
            v-for="item in academicItems"
            :key="item.id"
            :to="item.route"
            class="block"
            @click="handleNavClick(item.id)"
          >
            <div
              :class="[
                'group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150',
                activeItem === item.id
                  ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/70 dark:hover:bg-slate-800/60',
                isCollapsed ? 'justify-center px-0' : '',
              ]"
              :title="isCollapsed ? item.label : ''"
            >
              <!-- Active Indicator Bar -->
              <div
                v-if="activeItem === item.id"
                class="absolute left-0 top-1.5 bottom-1.5 w-1 bg-indigo-600 dark:bg-indigo-400 rounded-r-full"
              ></div>

              <component
                :is="item.icon"
                :class="[
                  'w-5 h-5 shrink-0 transition-colors',
                  activeItem === item.id
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-700 dark:group-hover:text-slate-300',
                ]"
              />

              <span
                v-if="!isCollapsed"
                class="truncate flex-1 tracking-tight"
              >
                {{ item.label }}
              </span>

              <!-- Badge Count -->
              <span
                v-if="!isCollapsed && item.badge"
                class="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/60"
              >
                {{ item.badge }}
              </span>
            </div>
          </RouterLink>
        </div>

        <!-- 3. ADMINISTRATION SECTION -->
        <div class="space-y-1">
          <div
            v-if="!isCollapsed"
            class="px-3 pb-1 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider"
          >
            Administration
          </div>
          <div
            v-else
            class="w-full h-px bg-slate-200 dark:bg-slate-800 my-2"
          ></div>

          <RouterLink
            v-for="item in administrationItems"
            :key="item.id"
            :to="item.route"
            class="block"
            @click="handleNavClick(item.id)"
          >
            <div
              :class="[
                'group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150',
                activeItem === item.id
                  ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/70 dark:hover:bg-slate-800/60',
                isCollapsed ? 'justify-center px-0' : '',
              ]"
              :title="isCollapsed ? item.label : ''"
            >
              <!-- Active Indicator Bar -->
              <div
                v-if="activeItem === item.id"
                class="absolute left-0 top-1.5 bottom-1.5 w-1 bg-indigo-600 dark:bg-indigo-400 rounded-r-full"
              ></div>

              <component
                :is="item.icon"
                :class="[
                  'w-5 h-5 shrink-0 transition-colors',
                  activeItem === item.id
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-700 dark:group-hover:text-slate-300',
                ]"
              />

              <span
                v-if="!isCollapsed"
                class="truncate flex-1 tracking-tight"
              >
                {{ item.label }}
              </span>

              <span
                v-if="!isCollapsed && item.badge"
                class="ml-auto text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300"
              >
                {{ item.badge }}
              </span>
            </div>
          </RouterLink>
        </div>

        <!-- 4. SYSTEM & SUPPORT -->
        <div class="space-y-1 pt-2">
          <RouterLink
            v-for="item in systemItems"
            :key="item.id"
            :to="item.route"
            class="block"
            @click="handleNavClick(item.id)"
          >
            <div
              :class="[
                'group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150',
                item.id === 'logout'
                  ? 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30'
                  : activeItem === item.id
                  ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/70 dark:hover:bg-slate-800/60',
                isCollapsed ? 'justify-center px-0' : '',
              ]"
              :title="isCollapsed ? item.label : ''"
            >
              <component
                :is="item.icon"
                :class="[
                  'w-5 h-5 shrink-0 transition-colors',
                  item.id === 'logout'
                    ? 'text-rose-500'
                    : activeItem === item.id
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-700 dark:group-hover:text-slate-300',
                ]"
              />

              <span
                v-if="!isCollapsed"
                class="truncate flex-1 tracking-tight"
              >
                {{ item.label }}
              </span>
            </div>
          </RouterLink>
        </div>
      </nav>

      <!-- FOOTER: User Card & Collapse Trigger -->
      <div
        class="mt-auto p-3 border-t border-slate-100 dark:border-[#1E293B] bg-slate-50/50 dark:bg-[#0B1120]/50 shrink-0 space-y-2"
      >
        <!-- User Info Tile -->
        <div
          v-if="!isCollapsed"
          class="flex items-center gap-3 p-2 rounded-xl bg-white dark:bg-[#111C30] border border-slate-200/70 dark:border-[#1E293B] shadow-xs"
        >
          <div
            class="relative w-8 h-8 rounded-lg overflow-hidden bg-indigo-100 dark:bg-indigo-950/70 border border-indigo-200 dark:border-indigo-800/50 flex items-center justify-center shrink-0"
          >
            <img
              v-if="userPhoto"
              :src="userPhoto"
              alt="Avatar"
              class="w-full h-full object-cover"
            />
            <User v-else class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>

          <div class="flex-1 min-w-0">
            <p class="text-xs font-bold text-slate-900 dark:text-white truncate leading-tight">
              {{ userName || "Administrator" }}
            </p>
            <p class="text-[10px] text-slate-500 dark:text-slate-400 truncate capitalize mt-0.5">
              {{ UserRole || "Staff" }}
            </p>
          </div>
        </div>

        <!-- Desktop Collapse Toggle Button -->
        <button
          type="button"
          @click="toggleCollapse"
          class="hidden lg:flex w-full items-center justify-center p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-800/70 border border-slate-200/60 dark:border-[#1E293B] transition-all cursor-pointer"
          :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        >
          <PanelLeftClose v-if="!isCollapsed" class="w-4 h-4" />
          <PanelLeftOpen v-else class="w-4 h-4" />
          <span v-if="!isCollapsed" class="text-xs font-semibold ml-2">
            Collapse Sidebar
          </span>
        </button>
      </div>
    </aside>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from "vue";
  import router from "@/router";
  import { useRoute } from "vue-router";
  import { useStore } from "vuex";
  import {
    Home,
    Users,
    UserCheck,
    ClipboardCheck,
    HelpCircle,
    LogOut,
    GraduationCap,
    User,
    School,
    FileText,
    Sparkles,
    Cpu,
    Globe,
    Wallet,
    Server,
    X,
    PanelLeftClose,
    PanelLeftOpen,
  } from "lucide-vue-next";
  import { useSidebar } from "@/composables/useSidebar";

  const store = useStore();
  const route = useRoute();
  const { isCollapsed, showMobileSidebar, toggleCollapse, closeMobileSidebar } =
    useSidebar();

  // Active Item based on Route
  const activeItem = computed(() => {
    const path = route.path;

    if (path.startsWith("/students")) return "students";
    if (path.startsWith("/teacherAttendance")) return "teacherAttendance";
    if (path.startsWith("/teacher")) return "teachers";
    if (path.startsWith("/classes")) return "classes";
    if (path.startsWith("/leaveRequest")) return "leaveRequests";
    if (path.startsWith("/attendance")) return "attendance";
    if (path.startsWith("/report")) return "reports";
    if (path.toLowerCase().startsWith("/help")) return "help";
    if (path.startsWith("/device-operations")) return "deviceOperations";
    if (path.startsWith("/features")) return "features";
    if (path.startsWith("/fee-management") || path.startsWith("/fees"))
      return "feeManagement";
    if (path.startsWith("/devices")) return "devices";
    if (path.startsWith("/WEBDashboard")) return "webDashboard";
    if (path === "/" || path === "") return "dashboard";

    return null;
  });

  onMounted(() => {
    store.dispatch("fetchAllClasses");
    store.dispatch("fetchTeachers", { page: 1, limit: 10, search: "" });
    store.dispatch("initializeUserRole");
    store.dispatch("fetchStudents");
    store.dispatch("fetchCurrentUser");
  });

  // Computed state
  const TotalTeachers = computed(() => store.getters.getTotalTeachers);
  const TotalClasses = computed(() => {
    const classes = store.getters.allClasses || [];
    return classes.filter((c) => (c?.name || "").toLowerCase() !== "passout")
      .length;
  });
  const TotalStudents = computed(() => store.getters.getTotalStudents);
  const userName = computed(() => store.getters.getUserName);
  const UserRole = computed(() => store.getters.getUserRole);
  const currentUser = computed(() => store.state.currentUserDetails);

  const userPhoto = computed(() => {
    const photo = currentUser.value?.photo;
    const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";
    return photo
      ? photo.startsWith("http")
        ? photo
        : `${baseURL}/${photo}`
      : null;
  });

  // Nav Groups
  const mainNavigationItems = ref([
    {
      id: "dashboard",
      label: "Dashboard",
      icon: Home,
      route: "/",
    },
  ]);

  const academicItems = computed(() => {
    const items = [
      {
        id: "students",
        label: "Students",
        icon: Users,
        badge: TotalStudents.value,
        route: "/students",
      },
      {
        id: "teachers",
        label: "Teachers",
        icon: UserCheck,
        badge: TotalTeachers.value,
        route: "/teacher",
      },
      {
        id: "classes",
        label: "Classes",
        icon: School,
        badge: TotalClasses.value,
        route: "/classes",
      },
      {
        id: "attendance",
        label: "Student Attendance",
        icon: ClipboardCheck,
        badge: "Today",
        route: "/attendance",
      },
      {
        id: "teacherAttendance",
        label: "Teacher Attendance",
        icon: ClipboardCheck,
        badge: "Today",
        route: "/teacherAttendance",
      },
      {
        id: "leaveRequests",
        label: "Leave Requests",
        icon: FileText,
        route: "/leaveRequest",
      },
    ];

    if (UserRole.value === "teacher") {
      return items.filter(
        (item) =>
          item.id !== "teachers" &&
          item.id !== "teacherAttendance" &&
          item.id !== "devices",
      );
    }

    return items;
  });

  const administrationItems = computed(() => {
    const items = [];

    if (UserRole.value === "admin") {
      items.push({
        id: "feeManagement",
        label: "Fee Management",
        icon: Wallet,
        route: "/fee-management",
      });
    }

    items.push(
      {
        id: "reports",
        label: "Announcements",
        icon: ClipboardCheck,
        route: "/report",
      },
      {
        id: "devices",
        label: "Smart Cards & Devices",
        icon: Cpu,
        route: "/devices",
      },
      {
        id: "deviceOperations",
        label: "Device Operations",
        icon: Server,
        route: "/device-operations",
      },
      {
        id: "webDashboard",
        label: "WEB Dashboard",
        icon: Globe,
        route: "/WEBDashboard",
      },
      {
        id: "features",
        label: "Features",
        icon: Sparkles,
        route: "/features",
        badge: "New",
      },
    );

    if (UserRole.value === "teacher") {
      return items.filter(
        (item) =>
          item.id !== "devices" &&
          item.id !== "feeManagement" &&
          item.id !== "deviceOperations",
      );
    }

    return items;
  });

  const systemItems = ref([
    {
      id: "help",
      label: "Help & Support",
      icon: HelpCircle,
      route: "/Helpandsupport",
    },
    {
      id: "logout",
      label: "Sign Out",
      icon: LogOut,
      route: "/login",
    },
  ]);

  const handleNavClick = (itemId) => {
    closeMobileSidebar();
    if (itemId === "logout") {
      localStorage.clear();
      sessionStorage.clear();
      localStorage.removeItem("token");
      router.push("/login");
    }
  };
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(148, 163, 184, 0.6) transparent;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 5px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.5);
    border-radius: 9999px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.7);
  }
  html.dark .custom-scrollbar {
    scrollbar-color: rgba(71, 85, 105, 0.6) transparent;
  }
  html.dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(71, 85, 105, 0.5);
  }
  html.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(129, 140, 248, 0.7);
  }
</style>
